import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    Tab,
    Tabs,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Divider
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SyncIcon from '@mui/icons-material/Sync';
import InventoryIcon from '@mui/icons-material/Inventory';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { BASE_URL } from '../../../api/Custom';
import './scheduledJobs.css';

const ScheduledJobs = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [channels, setChannels] = useState([]);
    const [inventoryFeeds, setInventoryFeeds] = useState([]);
    const [error, setError] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [feedDetailsOpen, setFeedDetailsOpen] = useState(false);
    const [selectedFeedDetails, setSelectedFeedDetails] = useState(null);
    const [loadingFeedDetails, setLoadingFeedDetails] = useState(false);
    const [listingsDialogOpen, setListingsDialogOpen] = useState(false);
    const [activeListings, setActiveListings] = useState([]);
    const [listingsStats, setListingsStats] = useState(null);
    const [loadingListings, setLoadingListings] = useState(false);
    const [listingsJobInfo, setListingsJobInfo] = useState(null);
    const [reportQueue, setReportQueue] = useState([]);
    const [reportQueueStats, setReportQueueStats] = useState(null);

    const userId = localStorage.getItem('userId');
    const token = localStorage.getItem('token');
    const API_BASE_URL = BASE_URL;

    useEffect(() => {
        fetchAllData();
        
        // Auto-refresh every 30 seconds
        const interval = setInterval(() => {
            if (autoRefresh) {
                fetchAllData();
            }
        }, 30000);

        return () => clearInterval(interval);
    }, [autoRefresh]);

    const fetchAllData = async () => {
        try {
            setLoading(true);
            
            // Check if userId exists
            if (!userId) {
                setError('User ID not found. Please log in again.');
                setLoading(false);
                return;
            }
            
            const config = {
                headers: { 'Authorization': `Bearer ${token}` }
            };

            const [statsRes, jobsRes, channelsRes, feedsRes, queueRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/scheduler/stats/${userId}`, config),
                axios.get(`${API_BASE_URL}/api/scheduler/jobs/status/${userId}`, config),
                axios.get(`${API_BASE_URL}/api/scheduler/channels/${userId}`, config),
                axios.get(`${API_BASE_URL}/api/scheduler/inventory-feeds/${userId}`, config),
                axios.get(`${API_BASE_URL}/api/scheduler/report-queue/${userId}`, config)
            ]);

            setStats(statsRes.data);
            setJobs(jobsRes.data);
            setChannels(channelsRes.data);
            setInventoryFeeds(feedsRes.data);
            setReportQueue(queueRes.data.reportQueue || []);
            setReportQueueStats(queueRes.data.statistics || {});
            setError(null);
        } catch (err) {
            setError('Failed to fetch scheduler data: ' + (err.response?.data?.message || err.message));
            console.error('Error fetching scheduler data:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchActiveListings = async () => {
        try {
            setLoadingListings(true);
            setListingsDialogOpen(true);
            const config = {
                headers: { 'Authorization': `Bearer ${token}` }
            };

            console.log('Fetching active listings for userId:', userId);
            const response = await axios.get(
                `${API_BASE_URL}/api/scheduler/active-listings/${userId}`,
                config
            );

            console.log('Active Listings Response:', response.data);
            console.log('Listings count:', response.data.listings?.length || 0);
            console.log('Statistics:', response.data.statistics);
            console.log('Job Info:', response.data.jobInfo);

            setActiveListings(response.data.listings || []);
            setListingsStats(response.data.statistics || {});
            setListingsJobInfo(response.data.jobInfo || null);
        } catch (err) {
            console.error('Error fetching active listings:', err);
            console.error('Error response:', err.response?.data);
            setError('Failed to fetch active listings: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoadingListings(false);
        }
    };

    const fetchFeedDetails = async (feedId) => {
        try {
            setLoadingFeedDetails(true);
            setFeedDetailsOpen(true);
            const config = {
                headers: { 'Authorization': `Bearer ${token}` }
            };
            const response = await axios.get(
                `${API_BASE_URL}/api/scheduler/inventory-feeds/details/${feedId}`,
                config
            );
            setSelectedFeedDetails(response.data);
        } catch (err) {
            console.error('Error fetching feed details:', err);
            setSelectedFeedDetails({
                error: 'Failed to load feed details: ' + (err.response?.data?.message || err.message)
            });
        } finally {
            setLoadingFeedDetails(false);
        }
    };

    const handleCloseFeedDetails = () => {
        setFeedDetailsOpen(false);
        setSelectedFeedDetails(null);
    };

    const formatDateTime = (timestamp) => {
        if (!timestamp) return 'Never';
        const date = new Date(timestamp);
        return date.toLocaleString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const getJobDisplayName = (identifier) => {
        const jobNames = {
            'GET_ORDERS_FROM_CHANNELS': 'Order Pull Scheduler',
            'STOCK_UPDATE_IN_CHANNELS': 'Stock Update Scheduler',
            '_GET_MERCHANT_LISTINGS_DATA_': 'Active Listings Scheduler',
            'CHECK_ORDER_STATUS': 'Order Status Check',
            'BALANCE_CHECKING': 'Balance Checking Scheduler',
            'RUN_REPORT': 'Report Processing Scheduler'
        };
        return jobNames[identifier] || identifier;
    };

    const getJobFrequency = (identifier) => {
        const frequencies = {
            'GET_ORDERS_FROM_CHANNELS': 'Every 10 minutes',
            'STOCK_UPDATE_IN_CHANNELS': 'Every 10 minutes',
            '_GET_MERCHANT_LISTINGS_DATA_': 'Daily at 8:30 PM',
            'CHECK_ORDER_STATUS': 'Every 10 minutes',
            'BALANCE_CHECKING': 'Daily at 10:10 AM',
            'RUN_REPORT': 'As needed'
        };
        return frequencies[identifier] || 'Variable';
    };

    const getTimeSinceLastRun = (timestamp) => {
        if (!timestamp) return 'Never run';
        const now = new Date();
        const lastRun = new Date(timestamp);
        const diffMs = now - lastRun;
        const diffMins = Math.floor(diffMs / 60000);
        
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} min ago`;
        if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
        return `${Math.floor(diffMins / 1440)} days ago`;
    };

    const getFeedStatus = (status) => {
        const statusMap = {
            'DONE': { color: 'success', icon: <CheckCircleIcon />, label: 'Completed' },
            'IN_PROGRESS': { color: 'warning', icon: <SyncIcon className="rotating" />, label: 'Processing' },
            'SUBMITTED': { color: 'info', icon: <ScheduleIcon />, label: 'Submitted' },
            'FATAL': { color: 'error', icon: <ErrorIcon />, label: 'Failed' },
            'CANCELLED': { color: 'default', icon: <ErrorIcon />, label: 'Cancelled' }
        };
        return statusMap[status] || { color: 'default', icon: <ScheduleIcon />, label: status };
    };

    const StatCard = ({ title, value, icon, color }) => (
        <Card className="stat-card" sx={{ borderLeft: `4px solid ${color}` }}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography color="textSecondary" gutterBottom variant="body2">
                            {title}
                        </Typography>
                        <Typography variant="h4" component="div" fontWeight="bold">
                            {value}
                        </Typography>
                    </Box>
                    <Box className="stat-icon" sx={{ color }}>
                        {icon}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );

    if (loading && !stats) {
        return (
            <Box className="loading-container">
                <CircularProgress />
                <Typography variant="h6" mt={2}>Loading Scheduled Jobs...</Typography>
            </Box>
        );
    }

    return (
        <Box className="scheduled-jobs-container" p={3}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" fontWeight="bold">
                    <ScheduleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Scheduled Jobs Monitor
                </Typography>
                <Box>
                    <Tooltip title="Auto-refresh every 30 seconds">
                        <Chip
                            label={autoRefresh ? "Auto-refresh ON" : "Auto-refresh OFF"}
                            color={autoRefresh ? "success" : "default"}
                            onClick={() => setAutoRefresh(!autoRefresh)}
                            sx={{ mr: 1 }}
                        />
                    </Tooltip>
                    <Tooltip title="Refresh Now">
                        <IconButton onClick={fetchAllData} color="primary">
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Statistics Cards */}
            <Grid container spacing={3} mb={3}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Total Orders Synced"
                        value={stats?.totalOrders || 0}
                        icon={<ShoppingCartIcon fontSize="large" />}
                        color="#1976d2"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Today's Orders"
                        value={stats?.todayOrders || 0}
                        icon={<ShoppingCartIcon fontSize="large" />}
                        color="#2e7d32"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Active Channels"
                        value={stats?.activeChannels || 0}
                        icon={<SyncIcon fontSize="large" />}
                        color="#ed6c02"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Credit Balance"
                        value={stats?.creditBalance || 0}
                        icon={<AssessmentIcon fontSize="large" />}
                        color="#9c27b0"
                    />
                </Grid>
            </Grid>

            {/* Tabs */}
            <Paper sx={{ mb: 2 }}>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                    <Tab label="Scheduled Jobs" />
                    <Tab label="Inventory Sync Feeds" />
                    <Tab label="Channel Status" />
                    <Tab label="Report Request Queue" />
                </Tabs>
            </Paper>

            {/* Tab 1: Scheduled Jobs */}
            {activeTab === 0 && (
                <TableContainer component={Paper} className="table-container">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Job Name</strong></TableCell>
                                <TableCell><strong>Frequency</strong></TableCell>
                                <TableCell><strong>Channel</strong></TableCell>
                                <TableCell><strong>Last Run Time</strong></TableCell>
                                <TableCell><strong>Time Since Last Run</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell><strong>Message</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {jobs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography color="textSecondary">No scheduled jobs found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                jobs.map((job, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>
                                            {job.job_identifier || job.jobIdentifier}
                                            {(job.job_identifier === '_GET_MERCHANT_LISTINGS_DATA_' || job.jobIdentifier === '_GET_MERCHANT_LISTINGS_DATA_') && (
                                                <Tooltip title="View listings details">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => fetchActiveListings()}
                                                        sx={{ ml: 1 }}
                                                    >
                                                        <AssessmentIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={getJobFrequency(job.job_identifier || job.jobIdentifier)}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>{job.channel_id || job.channelId || 'All'}</TableCell>
                                        <TableCell>{formatDateTime(job.last_run_time || job.lastRunTime)}</TableCell>
                                        <TableCell>{getTimeSinceLastRun(job.last_run_time || job.lastRunTime)}</TableCell>
                                        <TableCell>
                                            {job.status ? (
                                                <Chip
                                                    icon={job.status === 'SUCCESS' ? <CheckCircleIcon /> : <ErrorIcon />}
                                                    label={job.status}
                                                    color={job.status === 'SUCCESS' ? 'success' : 'error'}
                                                    size="small"
                                                />
                                            ) : (
                                                <Chip
                                                    icon={<CheckCircleIcon />}
                                                    label="Active"
                                                    color="success"
                                                    size="small"
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {job.message && job.message.length > 100 ? (
                                                <Tooltip title={job.message} arrow placement="top">
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            maxWidth: '300px',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                color: 'primary.main',
                                                                textDecoration: 'underline'
                                                            }
                                                        }}
                                                    >
                                                        {job.message}
                                                    </Typography>
                                                </Tooltip>
                                            ) : (
                                                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                                    {job.message || '-'}
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Tab 2: Inventory Sync Feeds */}
            {activeTab === 1 && (
                <TableContainer component={Paper} className="table-container">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Feed ID</strong></TableCell>
                                <TableCell><strong>Channel</strong></TableCell>
                                <TableCell><strong>Feed Type</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell><strong>Items Processed</strong></TableCell>
                                <TableCell><strong>Success/Failed</strong></TableCell>
                                <TableCell><strong>Created Time</strong></TableCell>
                                <TableCell><strong>Completed Time</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {inventoryFeeds.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Typography color="textSecondary">No inventory feed data available</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                inventoryFeeds.map((feed, index) => {
                                    const feedStatus = getFeedStatus(feed.processingStatus);
                                    const successRate = feed.itemsProcessed > 0
                                        ? ((feed.itemsSuccessful / feed.itemsProcessed) * 100).toFixed(1)
                                        : 0;

                                    return (
                                        <TableRow key={index} hover>
                                            <TableCell>
                                                <Tooltip title="Click to view details">
                                                    <Typography 
                                                        variant="body2" 
                                                        color="primary" 
                                                        sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                        onClick={() => fetchFeedDetails(feed.id)}
                                                    >
                                                        {feed.feedId}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>{feed.channelName || 'N/A'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={<InventoryIcon />}
                                                    label={feed.feedType || 'Inventory Update'}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={feedStatus.icon}
                                                    label={feedStatus.label}
                                                    color={feedStatus.color}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{feed.itemsProcessed || 0}</TableCell>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" color="success.main">
                                                        ✓ {feed.itemsSuccessful || 0}
                                                    </Typography>
                                                    <Typography variant="body2" color="error.main">
                                                        ✗ {feed.itemsFailed || 0}
                                                    </Typography>
                                                    {feed.itemsProcessed > 0 && (
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={parseFloat(successRate)}
                                                            color={successRate >= 90 ? 'success' : successRate >= 70 ? 'warning' : 'error'}
                                                            sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
                                                        />
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>{formatDateTime(feed.createdTime)}</TableCell>
                                            <TableCell>{formatDateTime(feed.completedTime)}</TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Tab 3: Channel Status */}
            {activeTab === 2 && (
                <TableContainer component={Paper} className="table-container">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Channel</strong></TableCell>
                                <TableCell><strong>Channel Name</strong></TableCell>
                                <TableCell><strong>Order Sync</strong></TableCell>
                                <TableCell><strong>Stock Sync</strong></TableCell>
                                <TableCell><strong>Last Order Sync</strong></TableCell>
                                <TableCell><strong>Last Stock Sync</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {channels.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <Typography color="textSecondary">No channels configured</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                channels.map((channel, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>
                                            <Chip label={channel.channel} color="primary" size="small" />
                                        </TableCell>
                                        <TableCell>{channel.channel_name}</TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={channel.order_sync === 'Y' ? <CheckCircleIcon /> : <ErrorIcon />}
                                                label={channel.order_sync === 'Y' ? 'Enabled' : 'Disabled'}
                                                color={channel.order_sync === 'Y' ? 'success' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={channel.stock_sync === 'Y' ? <CheckCircleIcon /> : <ErrorIcon />}
                                                label={channel.stock_sync === 'Y' ? 'Enabled' : 'Disabled'}
                                                color={channel.stock_sync === 'Y' ? 'success' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {channel.lastOrderSync ? formatDateTime(channel.lastOrderSync) : 'N/A'}
                                        </TableCell>
                                        <TableCell>
                                            {channel.lastStockSync ? formatDateTime(channel.lastStockSync) : 'N/A'}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Tab 4: Report Request Queue */}
            {activeTab === 3 && (
                <Box>
                    {reportQueueStats && (
                        <Grid container spacing={2} mb={3}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card>
                                    <CardContent>
                                        <Typography color="textSecondary" variant="body2">
                                            Total Reports
                                        </Typography>
                                        <Typography variant="h4">
                                            {reportQueueStats.totalReports || 0}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card>
                                    <CardContent>
                                        <Typography color="textSecondary" variant="body2">
                                            Pending
                                        </Typography>
                                        <Typography variant="h4" color="warning.main">
                                            {reportQueueStats.pendingReports || 0}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card>
                                    <CardContent>
                                        <Typography color="textSecondary" variant="body2">
                                            Processing
                                        </Typography>
                                        <Typography variant="h4" color="info.main">
                                            {reportQueueStats.processingReports || 0}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card>
                                    <CardContent>
                                        <Typography color="textSecondary" variant="body2">
                                            Completed
                                        </Typography>
                                        <Typography variant="h4" color="success.main">
                                            {reportQueueStats.completedReports || 0}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}

                    <TableContainer component={Paper} className="table-container">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Report ID</strong></TableCell>
                                    <TableCell><strong>Report Type</strong></TableCell>
                                    <TableCell><strong>Channel</strong></TableCell>
                                    <TableCell><strong>Trigger</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                    <TableCell><strong>Records</strong></TableCell>
                                    <TableCell><strong>Created</strong></TableCell>
                                    <TableCell><strong>Processing Time</strong></TableCell>
                                    <TableCell><strong>Details</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reportQueue.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center">
                                            <Typography color="textSecondary">No reports in queue</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    reportQueue.map((report, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                {report.report_request_id}
                                            </TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={report.report_type || 'Unknown'} 
                                                    size="small" 
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {report.channel_name || report.channel || 'N/A'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={report.trigger_source || 'N/A'}
                                                    size="small"
                                                    color={report.trigger_source === 'MANUAL' ? 'secondary' : 'default'}
                                                    variant={report.trigger_source === 'MANUAL' ? 'filled' : 'outlined'}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {report.status ? (
                                                    <Chip 
                                                        label={report.status} 
                                                        size="small"
                                                        color={
                                                            report.status === 'DONE' ? 'success' :
                                                            report.status === 'IN_PROCESS' ? 'info' :
                                                            report.status === 'FAILED' ? 'error' :
                                                            'warning'
                                                        }
                                                    />
                                                ) : (
                                                    <Chip label="Pending" size="small" color="warning" />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {report.total_records_processed !== null && report.total_records_processed !== undefined ? 
                                                    report.total_records_processed : '-'}
                                            </TableCell>
                                            <TableCell>
                                                {report.created_dt_time ? new Date(report.created_dt_time).toLocaleString() : 'N/A'}
                                            </TableCell>
                                            <TableCell>
                                                {report.processing_started_time && report.processing_completed_time ? (
                                                    (() => {
                                                        const start = new Date(report.processing_started_time);
                                                        const end = new Date(report.processing_completed_time);
                                                        const diff = Math.round((end - start) / 1000); // seconds
                                                        return diff >= 60 ? `${Math.floor(diff / 60)}m ${diff % 60}s` : `${diff}s`;
                                                    })()
                                                ) : report.processing_started_time ? 'In Progress...' : '-'}
                                            </TableCell>
                                            <TableCell>
                                                {report.processing_details || report.error_message ? (
                                                    <Chip 
                                                        label="View" 
                                                        size="small" 
                                                        color="primary" 
                                                        variant="outlined"
                                                        onClick={() => {
                                                            alert(
                                                                (report.error_message ? 'ERROR: ' + report.error_message : '') +
                                                                (report.error_message && report.processing_details ? '\n\n' : '') +
                                                                (report.processing_details || '')
                                                            );
                                                        }}
                                                    />
                                                ) : '-'}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {/* Feed Details Dialog */}
            <Dialog 
                open={feedDetailsOpen} 
                onClose={handleCloseFeedDetails}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            <InventoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Feed Details
                        </Typography>
                        <IconButton onClick={handleCloseFeedDetails} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    {loadingFeedDetails ? (
                        <Box display="flex" justifyContent="center" p={3}>
                            <CircularProgress />
                        </Box>
                    ) : selectedFeedDetails?.error ? (
                        <Alert severity="error">{selectedFeedDetails.error}</Alert>
                    ) : selectedFeedDetails ? (
                        <Box>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="textSecondary">Feed ID</Typography>
                                    <Typography variant="body1" fontWeight="bold">{selectedFeedDetails.feedId}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="textSecondary">Channel</Typography>
                                    <Typography variant="body1">{selectedFeedDetails.channelName}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="textSecondary">Feed Type</Typography>
                                    <Typography variant="body1">{selectedFeedDetails.feedType || 'Inventory Update'}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="textSecondary">Sync Method</Typography>
                                    <Chip 
                                        label={
                                            selectedFeedDetails.syncMethod === 1 ? 'Method 1: Tab-Delimited' :
                                            selectedFeedDetails.syncMethod === 2 ? 'Method 2: JSON PARTIAL_UPDATE' :
                                            selectedFeedDetails.syncMethod === 3 ? 'Method 3: JSON Offers' :
                                            'Unknown'
                                        }
                                        color={
                                            selectedFeedDetails.syncMethod === 2 ? 'success' :
                                            selectedFeedDetails.syncMethod === 3 ? 'warning' : 'default'
                                        }
                                        size="small"
                                        sx={{ mt: 0.5 }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                                    <Chip 
                                        label={getFeedStatus(selectedFeedDetails.processingStatus).label}
                                        color={getFeedStatus(selectedFeedDetails.processingStatus).color}
                                        size="small"
                                        sx={{ mt: 0.5 }}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="subtitle2" color="textSecondary">Items Processed</Typography>
                                    <Typography variant="body1">{selectedFeedDetails.itemsProcessed || 0}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="subtitle2" color="textSecondary">Successful</Typography>
                                    <Typography variant="body1" color="success.main">{selectedFeedDetails.itemsSuccessful || 0}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="subtitle2" color="textSecondary">Failed</Typography>
                                    <Typography variant="body1" color="error.main">{selectedFeedDetails.itemsFailed || 0}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="textSecondary">Created Time</Typography>
                                    <Typography variant="body1">{formatDateTime(selectedFeedDetails.createdTime)}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="textSecondary">Completed Time</Typography>
                                    <Typography variant="body1">{formatDateTime(selectedFeedDetails.completedTime)}</Typography>
                                </Grid>
                                {selectedFeedDetails.resultDocumentId && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="textSecondary">Result Document ID</Typography>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{selectedFeedDetails.resultDocumentId}</Typography>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                        Submitted Feed Data
                                    </Typography>
                                    {selectedFeedDetails.feedContent ? (
                                        <Paper 
                                            sx={{ 
                                                p: 2, 
                                                backgroundColor: '#e8f5e9',
                                                maxHeight: '300px',
                                                overflow: 'auto',
                                                fontFamily: 'monospace',
                                                fontSize: '0.85rem',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-all'
                                            }}
                                        >
                                            {(() => {
                                                // Format JSON content for methods 2 and 3
                                                if (selectedFeedDetails.syncMethod === 2 || selectedFeedDetails.syncMethod === 3) {
                                                    try {
                                                        const jsonData = JSON.parse(selectedFeedDetails.feedContent);
                                                        return JSON.stringify(jsonData, null, 2);
                                                    } catch (e) {
                                                        return selectedFeedDetails.feedContent;
                                                    }
                                                }
                                                // Tab-delimited for method 1
                                                return selectedFeedDetails.feedContent;
                                            })()}
                                        </Paper>
                                    ) : (
                                        <Alert severity="info" sx={{ mt: 1 }}>
                                            Submitted feed data is not available for this feed. Feed content is only saved for feeds submitted after the latest system update.
                                        </Alert>
                                    )}
                                </Grid>
                                {selectedFeedDetails.resultContent && (
                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 2 }} />
                                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                            Feed Result Details
                                        </Typography>
                                        <Paper 
                                            sx={{ 
                                                p: 2, 
                                                backgroundColor: '#f5f5f5',
                                                maxHeight: '300px',
                                                overflow: 'auto',
                                                fontFamily: 'monospace',
                                                fontSize: '0.85rem',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-all'
                                            }}
                                        >
                                            {selectedFeedDetails.resultContent}
                                        </Paper>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    ) : (
                        <Typography>No details available</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseFeedDetails} variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Active Listings Details Dialog */}
            <Dialog
                open={listingsDialogOpen}
                onClose={() => setListingsDialogOpen(false)}
                maxWidth="xl"
                fullWidth
                PaperProps={{
                    sx: { bgcolor: 'background.paper' }
                }}
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Active Listings Details</Typography>
                        <IconButton onClick={() => setListingsDialogOpen(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ bgcolor: 'background.default', minHeight: '400px' }}>
                    {loadingListings ? (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                            <Typography ml={2}>Loading active listings...</Typography>
                        </Box>
                    ) : activeListings.length === 0 ? (
                        <Box display="flex" flexDirection="column" alignItems="center" p={4}>
                            <Typography variant="h6" color="textSecondary" gutterBottom>
                                No Active Listings Found
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Run the Active Listings Scheduler to fetch products from your channels.
                            </Typography>
                        </Box>
                    ) : (
                        <Box>
                            {/* Job Status Information */}
                            {listingsJobInfo && (
                                <Alert 
                                    severity={listingsJobInfo.status === 'SUCCESS' ? 'success' : 'error'} 
                                    sx={{ mb: 2 }}
                                    icon={listingsJobInfo.status === 'SUCCESS' ? <CheckCircleIcon /> : <ErrorIcon />}
                                >
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight="bold">
                                            Last Sync: {listingsJobInfo.lastRunTime ? formatDateTime(listingsJobInfo.lastRunTime) : 'Never'}
                                        </Typography>
                                        <Typography variant="body2">
                                            {listingsJobInfo.message || 'No status message available'}
                                        </Typography>
                                    </Box>
                                </Alert>
                            )}

                            {/* Statistics Cards */}
                            {listingsStats && (
                                <Grid container spacing={2} mb={3}>
                                    <Grid item xs={12} md={4}>
                                        <Card>
                                            <CardContent>
                                                <Typography color="textSecondary" variant="body2">
                                                    Total Listings
                                                </Typography>
                                                <Typography variant="h4">
                                                    {listingsStats.totalListings || 0}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Card>
                                            <CardContent>
                                                <Typography color="textSecondary" variant="body2">
                                                    With Product Type
                                                </Typography>
                                                <Typography variant="h4" color="success.main">
                                                    {listingsStats.listingsWithProductType || 0}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Card>
                                            <CardContent>
                                                <Typography color="textSecondary" variant="body2">
                                                    Without Product Type
                                                </Typography>
                                                <Typography variant="h4" color="warning.main">
                                                    {listingsStats.listingsWithoutProductType || 0}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            )}

                            {/* Listings Table */}
                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>Channel</strong></TableCell>
                                            <TableCell><strong>SKU</strong></TableCell>
                                            <TableCell><strong>Product Title</strong></TableCell>
                                            <TableCell><strong>Product Type</strong></TableCell>
                                            <TableCell><strong>Price</strong></TableCell>
                                            <TableCell><strong>Quantity</strong></TableCell>
                                            <TableCell><strong>FBA</strong></TableCell>
                                            <TableCell><strong>Last Updated</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {activeListings.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={8} align="center">
                                                    <Typography color="textSecondary">No listings found</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            activeListings.map((listing, index) => (
                                                <TableRow key={index} hover>
                                                    <TableCell>{listing.channel_name || listing.channel}</TableCell>
                                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                        {listing.channel_sku}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" noWrap sx={{ maxWidth: '200px' }}>
                                                            {listing.channel_product_title}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {listing.product_type ? (
                                                            <Chip 
                                                                label={listing.product_type} 
                                                                size="small" 
                                                                color="success" 
                                                                variant="outlined"
                                                            />
                                                        ) : (
                                                            <Chip 
                                                                label="Missing" 
                                                                size="small" 
                                                                color="warning" 
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {listing.channel_price ? `$${listing.channel_price}` : '-'}
                                                    </TableCell>
                                                    <TableCell>{listing.channel_quantity || 0}</TableCell>
                                                    <TableCell>
                                                        {listing.fulfilled_by_channel === 'Y' ? (
                                                            <Chip label="Yes" size="small" color="primary" />
                                                        ) : (
                                                            <Chip label="No" size="small" variant="outlined" />
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {listing.cud ? formatDateTime(listing.cud) : '-'}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setListingsDialogOpen(false)} variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ScheduledJobs;
